/**
 * Cliente HTTP do Back Office.
 *
 * Duas regras que este modulo respeita:
 *
 * 1. A extracao de mensagem de erro fica em UM lugar so. O playbook registra um
 *    bug que sobreviveu ao proprio conserto porque a regra estava duplicada em
 *    dois caminhos de fetch: o `useAuth` tinha a propria copia. Aqui existe uma
 *    funcao, e o teste afirma que todo caminho a usa.
 *
 * 2. A API responde `{ error: CODIGO, message: texto }`. Ler somente `error`
 *    mostraria o NOME DO STATUS como se fosse texto para o usuario.
 */

/**
 * Vazio por default = MESMA ORIGEM.
 *
 * Em producao o nginx serve o Back Office e a API sob o mesmo host, e em
 * desenvolvimento o proxy do Vite faz o mesmo. Cravar `http://localhost:3001`
 * aqui faria o cookie de sessao (httpOnly, same-site) nao viajar, e a tela
 * pareceria "sem autenticacao" por um motivo que nao existe em producao.
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? '';

export type ApiErrorShape = {
  readonly code: string;
  readonly message: string;
  readonly correlationId: string | null;
  readonly httpStatus: number;
  /**
   * O corpo CRU da recusa.
   *
   * Existe porque algumas recusas carregam mais do que um texto: a do filtro
   * invalido traz a lista de problemas, com caminho e o que fazer em cada um.
   * Fica aqui, no unico lugar que extrai erro, em vez de a tela refazer o
   * `fetch` para ler o corpo de novo. O playbook registra um bug que sobreviveu
   * ao proprio conserto porque a regra de extracao tinha uma segunda copia.
   *
   * OPCIONAL na forma de entrada: quem constroi o erro a mao (teste, caminho de
   * rede fora) nao tem corpo nenhum, e exigi-lo obrigaria dez arquivos a passar
   * `body: null` sem que isso significasse coisa alguma.
   */
  readonly body?: unknown;
};

export class ApiError extends Error {
  readonly code: string;
  readonly correlationId: string | null;
  readonly httpStatus: number;
  readonly body: unknown;

  constructor(shape: ApiErrorShape) {
    super(shape.message);
    this.name = 'ApiError';
    this.code = shape.code;
    this.correlationId = shape.correlationId;
    this.httpStatus = shape.httpStatus;
    this.body = shape.body ?? null;
  }
}

/**
 * UNICA extracao de erro do projeto.
 *
 * Ordem deliberada: `message` primeiro, porque e o texto escrito para a pessoa;
 * `error` e o CODIGO e serve de fallback so quando nao ha mensagem. Inverter
 * isso e o que faz a tela mostrar "Unauthorized" para o usuario final.
 */
export function extractApiError(body: unknown, httpStatus: number): ApiErrorShape {
  const asRecord = (typeof body === 'object' && body !== null ? body : {}) as Record<
    string,
    unknown
  >;
  const message =
    typeof asRecord.message === 'string' && asRecord.message.length > 0
      ? asRecord.message
      : fallbackMessageFor(httpStatus);
  const code =
    typeof asRecord.error === 'string' && asRecord.error.length > 0
      ? asRecord.error
      : `HTTP_${httpStatus}`;
  const correlationId =
    typeof asRecord.correlation_id === 'string' ? asRecord.correlation_id : null;
  return { code, message, correlationId, httpStatus, body };
}

/** Texto em portugues quando o servidor nao mandou mensagem alguma. */
function fallbackMessageFor(httpStatus: number): string {
  if (httpStatus === 401) return 'Sessão expirada ou ausente. Entre novamente.';
  if (httpStatus === 403) return 'Sem permissão para esta ação.';
  if (httpStatus === 404) return 'Não encontrado ou não visível para este acesso.';
  if (httpStatus === 409) return 'O estado mudou. Recarregue e tente novamente.';
  if (httpStatus >= 500) return 'Serviço indisponível no momento.';
  return 'Não foi possível completar a operação.';
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  const text = await response.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    // Todo caminho passa por aqui: sem copia da regra em outro lugar.
    throw new ApiError(extractApiError(body, response.status));
  }
  return body as T;
}

export type OperationsResponse = {
  environment: string;
  /** Eixo de PAUSA: alguem apertou parar? Nao e o que a tela mostra. */
  system_state: string;
  /**
   * O estado que a tela MOSTRA, derivado no servidor de pausa mais interruptor
   * de efeito externo. Existe porque `system_state: running` significa apenas
   * "sem pausa", e a tela o promovia a "Sistema em operacao" num ambiente onde
   * nada pode publicar, gastar ou enviar.
   */
  operational_state: 'paused' | 'partially_paused' | 'no_external_effect' | 'running';
  /** O motor dirige a cadeia. Desligado, nenhuma fila existe. */
  motor_enabled: boolean;
  external_effect_switches: Record<string, boolean>;
  active_pauses: {
    scope: string;
    scope_id: string | null;
    reason: string;
    paused_at: string;
    revision: number;
  }[];
  projections: {
    name: string;
    version: string;
    freshness: string;
    last_processed_at: string | null;
  }[];
  projections_state: string;
  capabilities: {
    provider: string;
    capability: string;
    /** Veredito operacional: posso chamar? */
    state: string;
    /** Diagnóstico do Console SPEC §139: por que não? */
    status: string;
    validated_at: string | null;
    fallback: string;
  }[];
};

export type TitularExportResponse = {
  handle: string;
  generated_at: string;
  rows_returned: number;
  interactions: Record<string, unknown>[];
  responses: Record<string, unknown>[];
  audience_signals: Record<string, unknown>[];
  commercial_leads: Record<string, unknown>[];
  /** O que o documento NAO contem, dito no proprio documento. */
  limits: string[];
};

export type ConfigurationResponse = {
  parameters: {
    parameter_key: string;
    display_name: string;
    description: string;
    class: string;
    unit: string | null;
    owner: string;
    spec_ref: string;
    secret: boolean;
    value: unknown;
    status: string;
    fail_closed_default: unknown;
    version: number | null;
    /**
     * A próxima mudança marcada, quando existe.
     *
     * Separada do valor vigente de propósito: juntar as duas faria a tela
     * mostrar um número que ainda não vale como se valesse. Chave secreta traz
     * a data e a versão, e `value` nulo.
     */
    scheduled_change: {
      value: unknown;
      version: number;
      effective_from: string;
    } | null;
    changeable_by_actor: boolean;
    /**
     * Faixa declarada no registro. Serve para a tela dizer o limite ANTES de a
     * pessoa tentar: recusar depois de digitar e pior do que avisar antes.
     *
     * `pair` significa que o valor e `[minimo, maximo]`, como nas cercas de
     * calibracao do Radar.
     */
    allowed_range: {
      min?: number;
      max?: number;
      enum?: string[];
      pair?: { min: number; max: number };
      /** O valor é uma LISTA, e cada item vem deste enum. */
      list?: { enum: string[] };
    } | null;
  }[];
};

/**
 * Resposta da fronteira de comando.
 *
 * `status` e o ACEITE; `execution` e a APLICACAO. Sao coisas diferentes de
 * proposito (D-047), e a tela mostra as duas: um comando aceito cuja aplicacao
 * falhou nao pode aparecer como sucesso.
 */
export type CommandResponse = {
  command_id: string;
  status: string;
  accepted_at: string | null;
  denial_code: string | null;
  message: string | null;
  impact_summary: Record<string, unknown> | null;
  correlation_id: string;
  execution: { state: string; summary: string } | null;
};

export type KpisResponse = {
  kpis: {
    kpi_id: string;
    name: string;
    definition: string;
    unit: string;
    objective_direction: string;
    owner: string;
    definition_version: string;
    composite: boolean;
    quality_rule: string;
    status: string;
    value: number | null;
    /**
     * Cobertura de classificação do indicador, quando ele tem uma.
     *
     * Hoje só o AQI (KPI-001). Vem junto também nos estados sem nota: é ela que
     * explica por que a nota não aparece.
     */
    coverage: {
      classified_coverage: number;
      minimum_classified_coverage: number | null;
      minimum_profile_confidence: number | null;
      considered_profiles: number;
      qualified_profiles: number;
      reason: string;
    } | null;
  }[];
};

export type AgentsResponse = {
  publication_credential_holder: string;
  agents: {
    id: string;
    spec_number: string;
    domain: string;
    display_name: string;
    runtime_module: string;
    owns: string[];
    may_veto: string[];
    prohibited: string[];
    can_cause_public_effect: boolean;
    can_spend_money: boolean;
  }[];
  gates: {
    gate_type: string;
    display_name: string;
    protects: string;
    fail_closed_outcome: string;
    reason_codes: string[];
    exceptionable: boolean;
  }[];
};

/**
 * A situacao do motor.
 *
 * `ligado: false` NAO e erro: e o estado configurado, e a tela mostra o motivo
 * em vez de desenhar zeros que pareceriam motor sem trabalho.
 */
export type MotorResponse = {
  ligado: boolean;
  motivo: string | null;
  estagios_ligados: string[];
  filas: {
    nome: string;
    gatilho: string;
    tem_trabalhador: boolean;
    tentativas: number;
    pendentes: number;
    ativos: number;
    falhos: number;
    mortos: number;
  }[];
  achados: {
    fila: string;
    tipo: string;
    quantidade: number;
    o_que_fazer: string;
    quem_resolve: string;
  }[];
  mortos: number;
};

export type AttentionResponse = {
  derived_at: string;
  counts: Record<string, number>;
  total: number;
  visible_to_actor: number;
  items: {
    attention_id: string | null;
    condition_type: string;
    condition_id: string;
    severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
    summary: string;
    required_scopes: string[];
    due_at: string | null;
    options: string[];
    state: 'open' | 'acknowledged';
    acknowledged_at: string | null;
    acknowledged_by: string | null;
  }[];
};

export type ContentListResponse = {
  total: number;
  by_state: Record<string, number>;
  items: {
    content_id: string;
    topic: string;
    format: string;
    objective: string;
    state: string;
    state_reason: string | null;
    state_reason_code: string | null;
    valid_until: string | null;
    strategic_agenda_ids: string[];
    revision: number;
    updated_at: string;
    correlation_id: string;
  }[];
};

export type ContentDetailResponse = {
  content_id: string;
  topic: string;
  state: string;
  state_reason: string | null;
  revision: number;
  objective: string;
  format: string;
  valid_until: string | null;
  correlation_id: string;
  opportunity: {
    id: string;
    source_type: string;
    timeliness: string;
    why_it_matters: string;
    insight: string;
    score_opportunity: number | null;
    /** Por que nao houve nota. Nulo quando houve. */
    score_unavailable_reason: string | null;
    score_weights_version: string | null;
    expires_at: string | null;
  };
  editorial_decision: {
    id: string;
    disposition: string;
    portfolio_bucket: string | null;
    priority: number | null;
    reason: string;
    override_reason: string | null;
  };
  script_versions: {
    id: string;
    version: number;
    state: string;
    change_reason: string | null;
    semantic_hash: string;
    first_person: boolean;
    generated_with_ai: boolean;
  }[];
  claims: {
    id: string;
    surface: string;
    claim_text: string;
    materiality: string;
    verification_status: string;
    valid_until: string | null;
  }[];
  governance: {
    bundles: {
      id: string;
      script_version_id: string;
      required_reviews: string[];
      state: string;
      revision_attempt: number;
    }[];
    reviews: {
      id: string;
      reviewer: string;
      result: string;
      policy_version: string;
      score: number | null;
      reason: string;
      model_version: string | null;
      prompt_version: string | null;
      script_version_id: string;
    }[];
  };
  gates: {
    gate_type: string;
    object_type: string;
    result: string;
    reason_code: string;
    reason: string;
    policy_version: string;
    expires_at: string | null;
    created_at: string;
  }[];
  decisions: {
    id: string;
    decision_type: string;
    agent: string;
    reason: string;
    policy_refs: string[];
    model_version: string | null;
    prompt_version: string | null;
    override: unknown;
    occurred_at: string;
  }[];
  production_assets: {
    id: string;
    type: string;
    version: number;
    state: string;
    subtitles_status: string;
    audio_status: string;
    visual_qa_status: string;
    semantic_hash: string | null;
  }[];
  publications: {
    package_id: string;
    record_id: string;
    state: string;
    platform_post_id: string | null;
    published_at: string | null;
    retry_count: number;
    hold_reason_code: string | null;
  }[];
  transitions: {
    from: string | null;
    to: string;
    reason_code: string;
    reason: string;
    actor: string;
    occurred_at: string;
  }[];
};

export type IntegrationsResponse = {
  window_days: number;
  since: string;
  providers: {
    provider: string;
    state: 'never_called' | 'healthy' | 'degraded' | 'failing';
    /** `null` quando NUNCA houve chamada. Não é zero: é ausência de medida. */
    total_calls: number | null;
    successes: number;
    failures: number;
    ambiguous: number;
    unresolved_external_effects: number;
    last_call_at: string | null;
    last_failure_at: string | null;
    last_failure_code: string | null;
  }[];
  capabilities: {
    provider: string;
    capability: string;
    state: string;
    /** O DIAGNÓSTICO (§139): `state` diz "posso chamar?", este diz "por quê não?". */
    status: string;
    /** O motivo em linguagem de operação, escrito pela sondagem. */
    notes: string | null;
    validated_at: string | null;
    /** Contra o quê a capacidade foi validada: qual conta, qual versão da API. */
    source: string;
    fallback: string | null;
  }[];
  unvalidated_capabilities: number;
  /** A sondagem da Meta existe neste ambiente? Fato do servidor, não dedução. */
  meta_probe: {
    available: boolean;
    ad_account_id: string | null;
    api_version: string | null;
    capabilities: string[];
  };
  /** O que a sondagem MEDIU sobre a conta. `null` quando ninguém sondou. */
  meta_ad_account: ContaDeAnuncioMedida | null;
};

/**
 * Moeda, fuso e meio de pagamento da conta de anúncios, como a sondagem os leu.
 *
 * Aparecem na tela porque o resto do sistema hoje os ASSUME: o adaptador de
 * mídia paga compara moeda antes de gastar e o de Insights alinha janela por dia
 * presumindo UTC. Ver o valor medido é o que permite alguém notar que a conta
 * está em outra moeda antes da primeira campanha rodar.
 */
export type ContaDeAnuncioMedida = {
  currency: string | null;
  timezone_name: string | null;
  timezone_offset_hours_utc: number | null;
  has_funding_source: boolean | null;
  account_status: number | null;
  read_at: string | null;
};

export type SondagemDaMetaResponse = {
  probed_at: string;
  api_version: string;
  ad_account_id: string;
  results: {
    capability: string;
    status: string;
    /**
     * A leitura ACONTECEU?
     *
     * É o que separa "sondado e indisponível" de "não sondado": a primeira pede
     * que alguém resolva a restrição na conta, a segunda que alguém conecte a
     * conta. Sem este campo as duas apareceriam como "indisponível".
     */
    read_ok: boolean;
    notes: string;
  }[];
  ad_account: ContaDeAnuncioMedida | null;
};

export type RendersResponse = {
  total: number;
  by_state: Record<string, number>;
  needs_reconciliation: number;
  jobs: {
    job_id: string;
    asset_id: string;
    provider: string;
    state: string;
    remote_job_id: string | null;
    reserved_cents: number;
    attempts: number;
    last_polled_at: string | null;
    completed_at: string | null;
    failure_code: string | null;
    failure_reason: string | null;
    detected_artifacts: string[];
    created_at: string;
    correlation_id: string;
  }[];
};

/** Proveniência de um número. Nunca um número solto. */
export type MetricValue =
  | { kind: 'measured'; value: number }
  | { kind: 'estimated'; value: number; method: string; confidence: number }
  | { kind: 'not_measured'; reason: string };

export type PublicationMetricsResponse = {
  publication_id: string;
  state: string;
  published_at: string | null;
  platform_post_id: string | null;
  captured_ages: string[];
  pending_ages: string[];
  snapshots: {
    snapshot_id: string;
    age: string;
    captured_at: string;
    missing_fields: string[];
    metrics: Record<string, MetricValue>;
    estimated_qualified_follows: MetricValue;
  }[];
};

export type EpisodesResponse = {
  total: number;
  episodes: {
    episode_id: string;
    guest_name: string;
    company_name: string;
    release_status: string;
    status: string;
    scheduled_publish_at: string | null;
    prelaunch_authorized_classes: string[];
    prelaunch_start_at: string | null;
    prelaunch_end_at: string | null;
    rights_exceptions: {
      scope: string;
      effect: string;
      reason: string;
      valid_until: string;
    }[];
    cut_count: number;
    release_by_class: {
      content_class: string;
      can_process: boolean;
      can_publish: boolean;
      reason_code: string;
      reason: string;
    }[];
  }[];
};

export type LearningResponse = {
  signals: {
    signal_id: string;
    observation: string;
    evidence_refs: string[];
    sample_size: number;
    strength: string;
    confounders: string[];
    created_at: string;
  }[];
  recommendations: {
    recommendation_id: string;
    target_parameter: string;
    current_value: unknown;
    proposed_value: unknown;
    rationale: string;
    auto_applicable: boolean;
    auto_apply_blocked_reason: string | null;
    state: string;
    created_at: string;
  }[];
  changes: {
    change_id: string;
    parameter_key: string;
    previous_value: unknown;
    new_value: unknown;
    applied_by: string;
    applied_at: string;
    monitor_metric: string;
    monitor_window_end: string;
    /** `null` = aplicada sem baseline: nao ha contra o que comparar. */
    monitor_baseline: number | null;
    state: string;
    rolled_back_at: string | null;
    rollback_reason: string | null;
  }[];
  owner_requests: {
    request_id: string;
    requested_by: string;
    request_type: string;
    target_parameter: string | null;
    current_value: unknown;
    requested_value: unknown;
    rationale: string;
    evidence_refs: string[];
    created_at: string;
  }[];
  auto_apply_allowlist: string[];
};

export type AudienceResponse = {
  cohorts: {
    cohort_id: string;
    name: string;
    definition: string;
    value_tier: string;
    /** Medida pelo provedor. `null` = nao devolvida; NUNCA zero por default. */
    observed_count: number | null;
    /** Extrapolada. So vale com metodo e confianca. Nunca somada a observada. */
    estimated_count: number | null;
    estimation_method: string | null;
    estimation_confidence: number | null;
    source: string;
    captured_at: string;
  }[];
  experiments: {
    experiment_id: string;
    name: string;
    hypothesis: string;
    isolated_variable: string;
    changed_variables: string[];
    assigned_objective: string;
    primary_metric: string;
    state: string;
    isolation_valid: boolean;
    isolation_note: string | null;
    conclusion: string | null;
    conclusion_strength: string | null;
    minimum_sample_size: number;
    created_at: string;
  }[];
};

// ---------------------------------------------------------------------------
// Audience & Lists (Console SPEC v1.5 §177 a §185, §191 a §193)
// ---------------------------------------------------------------------------

/**
 * Os sete numeros do §183, mais a quebra por motivo de exclusao.
 *
 * `excluded_by_reason` traz `marked` e `unknown` SEPARADOS de proposito: o
 * primeiro e a regra funcionando, o segundo e um campo que falta na fonte. Um
 * numero so esconderia que a maior parte das exclusoes veio de ignorancia
 * nossa, e ninguem iria atras do campo.
 */
export type SegmentPreviewResponse = {
  total_records: number;
  records_with_email: number;
  records_with_phone: number;
  records_with_both: number;
  eligible_after_hard_exclusions: number;
  estimated_records_for_meta_upload: number;
  /** `null` quando a fonte nao reporta a data. Nunca "agora" inventado. */
  last_source_update_at: string | null;
  excluded_by_reason: Record<string, { marked: number; unknown: number }>;
};

/**
 * Por que a previa nao saiu.
 *
 * Quatro estados de ausencia, e nao um erro generico: cada um pede uma acao
 * diferente de quem opera, e colapsar os quatro apagaria a acao.
 */
export type PreviewState =
  'ready' | 'no_connector' | 'source_not_approved' | 'capability_missing' | 'source_error';

export type PreviewEnvelope = {
  preview_state: PreviewState;
  reason: string | null;
  preview: SegmentPreviewResponse | null;
};

export type FilterProblem = {
  path: string;
  code: string;
  detail: string;
  /** A frase em linguagem de operacao. E ela que aparece na tela. */
  what_to_do: string;
};

export type AudienceSourcesResponse = {
  sources: {
    source_id: string;
    logical_source_name: string;
    provider_name: string | null;
    source_type: string;
    integration_mode: string;
    status: string;
    approval_status: string;
    approval_scope: string;
    capabilities: Record<string, boolean>;
    hard_exclusions: Record<string, boolean>;
    available_fields: {
      name: string;
      label: string;
      type: string;
      filterable: boolean;
      usable_for_matching: boolean;
    }[];
    /** Entradas do catalogo de campos que estavam fora do formato. */
    discarded_fields: number;
    last_sync_at: string | null;
    last_health_check_at: string | null;
    /**
     * Existe codigo capaz de LER desta fonte.
     *
     * Eixo separado de `status` e de `approval_status`: uma fonte pode estar
     * conectada e aprovada e ainda assim nao ter conector, e nesse caso nao ha
     * numero para mostrar. Zero seria lido como "o filtro nao casou ninguem".
     */
    connector_installed: boolean;
    segment_count: number;
  }[];
  connectors_installed: number;
};

export type AudienceSegmentsResponse = {
  segments: {
    segment_id: string;
    name: string;
    description: string | null;
    source_id: string;
    logical_source_name: string | null;
    mode: string;
    filter_definition: unknown;
    filter_problems: FilterProblem[];
    imported_list_id: string | null;
    /** `null` = NUNCA avaliado. Zero seria "o filtro nao casou ninguem". */
    record_count_last_run: number | null;
    eligible_record_count: number | null;
    aqi: number | null;
    aqi_coverage: number | null;
    status: string;
    created_by: string;
    created_at: string;
    last_refreshed_at: string | null;
    last_evaluation: {
      evaluation_id: string;
      evaluated_at: string;
      requested_by: string;
      total_records: number;
      records_with_email: number;
      records_with_phone: number;
      records_with_both: number;
      eligible_after_hard_exclusions: number;
      estimated_records_for_meta_upload: number;
      excluded_by_reason: unknown;
      last_source_update_at: string | null;
    } | null;
  }[];
};

// ---- Importacao de audiencia por arquivo (Console SPEC v1.5 §186, §187) ----

/** De qual COLUNA do arquivo sai cada campo do catalogo do §187. */
export type MapeamentoDeColunasDaImportacao = Readonly<Record<string, string>>;

export type CorpoDaPreviaDeImportacao = {
  filename: string;
  file_type: 'CSV' | 'XLSX';
  /**
   * O texto do arquivo, lido no NAVEGADOR.
   *
   * Nao existe upload multipart nesta API, e isso e deliberado do lado de la:
   * multipart exigiria uma dependencia nova no grafo do app, e CSV e texto. A
   * tela le com a API de arquivo do navegador e manda o conteudo pelo mesmo
   * canal cifrado.
   */
  content: string;
  column_mapping: MapeamentoDeColunasDaImportacao;
};

/** Uma linha da amostra, SEM identificador nenhum dentro. */
export type LinhaDaAmostraDaImportacao = {
  line: number;
  verdict: string;
  notes: string[];
  /**
   * Cidade, cargo, empresa: o que NAO casa pessoa na plataforma.
   *
   * E por aqui que se confere que a coluna certa foi mapeada, pelo FORMATO do
   * que aparece. E-mail e telefone nao vem, e a tela nao tem como mostra-los.
   */
  descriptive_fields: Record<string, string>;
  /** Quais identificadores a linha TEM, sem dizer quais sao. */
  identifiers_present: string[];
};

export type AudienceImportPreviewResponse = {
  /** As colunas do arquivo, como a API as leu. */
  headers: string[];
  /** O catalogo do §187. Vem daqui, e nao de uma lista escrita no front. */
  mappable_fields: string[];
  /** O subconjunto que casa pessoa na plataforma. */
  matching_fields: string[];
  counts: {
    total: number;
    valid: number;
    warning: number;
    invalid: number;
    duplicated_in_file: number;
  };
  /**
   * O numero que a tela ECOA e que volta ao servidor no commit.
   *
   * Diferente de `counts.total`: o total inclui linha repetida, linha sem
   * identificador e linha invalida. Este projeto ja mediu o custo de contar uma
   * coisa e enviar outra na propria audiencia: quatro selecionados, um enviado.
   */
  rows_that_will_be_imported: number;
  sample: LinhaDaAmostraDaImportacao[];
};

/** Uma recusa do 409: a tela mostra TODAS, com o motivo de cada uma. */
export type RecusaDaImportacao = { code: string; reason: string };

/**
 * O corpo do 409 da prévia.
 *
 * `headers` PRESENTE quer dizer que o arquivo foi lido e quem falhou foi o
 * mapeamento: a tela tem colunas para oferecer, e a recusa mais comum daqui
 * ("nenhum campo de casamento foi mapeado") é o passo normal de quem acabou de
 * escolher um arquivo, e não um erro da pessoa.
 *
 * `headers` AUSENTE quer dizer que a leitura é que falhou (arquivo vazio, acima
 * do teto, sem cabeçalho). Aí não há coluna nenhuma a oferecer, e a distinção
 * importa: lista vazia faria a tela desenhar um seletor sem opção, como se o
 * arquivo não tivesse colunas.
 */
export type CorpoDaRecusaDaImportacao = {
  error: string;
  message: string;
  refusals?: RecusaDaImportacao[];
  headers?: string[];
  mappable_fields?: string[];
  matching_fields?: string[];
};

export type AudienceImportCommitResponse = {
  import_id: string;
  rows_imported: number;
  rows_total: number;
  rows_invalid: number;
  rows_duplicated: number;
};

export type AudienceImportsResponse = {
  imports: {
    id: string;
    filename: string;
    file_type: string;
    status: string;
    uploaded_at: string;
    rows_total: number;
    rows_valid: number;
    rows_warning: number;
    rows_invalid: number;
    dedupe_summary: unknown;
    /**
     * A AUSENCIA da declaracao e informacao, e nao um campo vazio.
     *
     * Lista sem declaracao existe e nao envia ninguem, porque as marcas de
     * exclusao ficam desconhecidas e desconhecido EXCLUI. Sem isto na tela, ela
     * pareceria pronta e ninguem entenderia por que o envio sai zerado.
     */
    lawful_basis_declared: boolean;
    lawful_basis_note: string | null;
  }[];
};

export type CommunityResponse = {
  total: number;
  by_classification: Record<string, number>;
  hidden_count: number;
  awaiting_human_count: number;
  interactions: {
    interaction_id: string;
    surface: string;
    author_handle: string;
    text: string;
    classification: string;
    risk_level: string;
    risk_triggers: string[];
    visibility: string;
    visibility_reason: string | null;
    visibility_policy_ref: string | null;
    received_at: string;
    response: {
      response_id: string;
      state: string;
      gate_result: string;
      gate_reason_code: string;
      gate_reason: string;
      draft_text: string;
      sent_text: string | null;
      sent_at: string | null;
    } | null;
  }[];
};

export type CommercialResponse = {
  total: number;
  opted_out_count: number;
  leads: {
    lead_id: string;
    contact_handle: string;
    state: string;
    state_reason: string | null;
    icp_fit: string;
    icp_evidence: string[];
    opted_out: boolean;
    opted_out_at: string | null;
    origin_interaction: {
      interaction_id: string;
      text: string;
      received_at: string;
    };
    follow_ups: {
      follow_up_id: string;
      sequence: number;
      new_value: string;
      state: string;
      blocked_reason: string | null;
      scheduled_for: string | null;
      sent_at: string | null;
    }[];
    meetings: {
      meeting_id: string;
      scheduled_for: string;
      duration_minutes: number;
      state: string;
      handoff_context: string;
      handoff_questions: string[];
      handoff_open_items: string[];
    }[];
    /** Elos FALTANTES nomeados: cadeia parcial nunca aparece como completa. */
    attribution: {
      content_id: string | null;
      publication_id: string | null;
      interaction_id: string;
      meeting_id: string | null;
      complete: boolean;
      missing_links: string[];
    };
  }[];
};

/**
 * Valor de KPI com a PROVENIENCIA junto.
 *
 * `value` e `null` em todo estado que nao seja medido, estimado ou velho. A
 * tela nunca precisa adivinhar se pode mostrar o numero.
 */
export type SerializedKpi = {
  kind:
    'measured' | 'estimated' | 'stale' | 'unavailable' | 'not_configured' | 'insufficient_sample';
  value: number | null;
  label: string;
  detail: string | null;
  captured_at: string | null;
};

export type ExecutiveResponse = {
  generatedAt: string;
  windowDays: number;
  freshnessSlaSeconds: number;
  kpis: Record<string, SerializedKpi>;
  agentPerformance: {
    agent: string;
    decisions: number;
    blockRate: SerializedKpi;
    overrideRate: SerializedKpi;
  }[];
  anomalies: { kpi: string; severity: string; reason: string }[];
};

/** Quem esta logado. `null` do lado do chamador significa "nao esta". */
export type MeResponse = {
  actor_id: string;
  display_name: string;
  roles: string[];
  /**
   * Os escopos que o SERVIDOR resolveu para este ator.
   *
   * A UI usa para esconder controle, e nunca como autorizacao: a autoridade
   * real e reavaliada em cada requisicao. Antes disto a UI INFERIA escopos das
   * chaves de configuracao que o ator podia alterar, e escopo de fora daquele
   * registro nunca aparecia.
   */
  scopes: string[];
  environment: string;
  expires_at: string;
};

// ===========================================================================
// Midia paga (Paid Media Module SPEC v1.0; Console SPEC v1.5 §236 a §258)
// ===========================================================================

/**
 * Dinheiro como o servidor manda: o inteiro E o texto.
 *
 * A tela NUNCA formata dinheiro e nunca faz conta com ele. O texto vem pronto
 * porque a formatação é escrita à mão num lugar só (`Intl` não é determinístico
 * entre ambientes), e o inteiro existe para a confirmação ecoar exatamente o
 * valor que a pessoa viu.
 */
export type Dinheiro = { minor: number; currency: string; formatado: string };

/** O estado de uma chave de configuração, com a frase que explica qual é. */
export type ValorNaTela = {
  valor: unknown;
  estado: 'active' | 'fail_closed_default' | 'not_configured';
  versao: number | null;
  explicacao: string;
};

export type ChecagemDoPortao = {
  linha: string;
  codigo: string;
  passou: boolean;
  detalhe: string;
  /** `null` quando a checagem passou. */
  o_que_fazer: string | null;
};

export type PortaoPago = {
  decision: string;
  reason_code: string;
  reason: string;
  /** As CATORZE, sempre. Nunca só as que falharam. */
  checks: ChecagemDoPortao[];
  total_checks: number;
  failed_checks: number;
  failed_codes: string[];
};

export type EnvelopeNaTela = {
  exists: boolean;
  /** Por que não existe. `null` quando existe. */
  reason: string | null;
  limit: Dinheiro | null;
  used: Dinheiro | null;
  available: Dinheiro | null;
  period_start_at: string | null;
  period_end_at: string | null;
};

export type ImpactoNoEnvelope = {
  fits: boolean;
  reserve: Dinheiro;
  available_before: Dinheiro | null;
  available_after: Dinheiro | null;
  reason: string | null;
};

export type CampanhaNaTela = {
  campaign_id: string;
  name: string;
  objective: string;
  plan_id: string | null;
  provider: string;
  ad_account_id: string | null;
  provider_campaign_id: string | null;
  provider_object_counts: { adsets: number; ads: number; creatives: number };
  budget: Dinheiro;
  spent: Dinheiro;
  requested_state: string;
  requested_state_label: string;
  requested_state_in_spec: string;
  spends_money: boolean;
  blocked_reason: string | null;
  /** §13: o que o provedor RESPONDE, ao lado do que pedimos. */
  provider_effective_state: string | null;
  provider_state_expected: string | null;
  provider_state_diverges: boolean;
  /** `true` = ninguém perguntou ao provedor ainda. Não é concordância. */
  never_reconciled: boolean;
  last_provider_sync_at: string | null;
  start_at: string | null;
  end_at: string | null;
  started_at: string | null;
  stopped_at: string | null;
  created_at: string;
};

export type PlanoNaTela = {
  plan_id: string;
  status: string;
  status_label: string;
  source_content_id: string;
  content: { topic: string; state: string } | null;
  growth_recommendation_id: string | null;
  audience_segment_id: string;
  audience: {
    name: string;
    status: string;
    /** `null` = nunca avaliado. Nunca zero. */
    record_count_last_run: number | null;
    eligible_record_count: number | null;
    aqi: number | null;
    aqi_coverage: number | null;
  } | null;
  meta_audience_binding: {
    binding_id: string | null;
    audience_name: string;
    status: string;
    last_sync_at: string | null;
    meta_ad_account_id: string;
  } | null;
  objective: string;
  objective_capability_version: string;
  budget: Dinheiro;
  budget_kind: string;
  budget_kind_label: string;
  start_at: string | null;
  end_at: string | null;
  paid_copy: string | null;
  paid_cta: string | null;
  governance: { triggers: string[]; cleared_at: string | null; pending: boolean };
  rights_paid_permission: string;
  rights_paid_permission_label: string;
  created_by_agent: string;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  decision_reason: string | null;
  created_at: string;
  campaigns: CampanhaNaTela[];
};

export type PaidMediaResponse = {
  module: {
    enabled: ValorNaTela;
    autonomy: ValorNaTela;
    auto_execution: ValorNaTela;
    /** O teto DECLARADO na configuração, que não é o envelope aprovado. */
    monthly_cap_declared: Dinheiro | null;
    monthly_cap_state: ValorNaTela['estado'];
    monthly_cap_explanation: string;
  };
  pauses: { system: string | null; paid_media: string | null };
  capabilities: {
    provider: string;
    capability: string;
    state: string;
    status: string;
    validated_at: string | null;
    fallback: string;
  }[];
  envelope: EnvelopeNaTela;
  /** Existe código capaz de falar com a Meta neste ambiente. */
  provider_configured: boolean;
  counts: {
    total: number;
    by_state: Record<string, number>;
    spending: number;
    paused: number;
    plans_awaiting_approval: number;
  };
  plans: PlanoNaTela[];
  campaigns: CampanhaNaTela[];
  wizard: {
    contents: { content_id: string; topic: string; lifecycle_state: string; updated_at: string }[];
    segments: {
      segment_id: string;
      name: string;
      status: string;
      record_count_last_run: number | null;
      eligible_record_count: number | null;
      aqi: number | null;
      aqi_coverage: number | null;
    }[];
    meta_audience_bindings: {
      binding_id: string;
      segment_id: string;
      audience_name: string;
      status: string;
      never_synced: boolean;
      last_sync_at: string | null;
      meta_ad_account_id: string;
    }[];
  };
};

/**
 * O catálogo de objetivos da conta conectada (Console SPEC §244).
 *
 * `objectives` é `null`, e nunca `[]`, quando não há catálogo: sem a conta
 * sondada a tela precisa voltar ao campo digitado dizendo por quê, e uma lista
 * vazia desenharia um seletor sem opção nenhuma, que se lê como "esta conta não
 * tem objetivo". `[]` só chega quando a conta respondeu e não habilita nada.
 */
export type ObjetivosDeCampanhaResponse = {
  objectives: { value: string; label: string }[] | null;
  /** Sob qual versão da capacidade a lista foi lida. `null` sem validação. */
  capability_version: string | null;
  capability: {
    provider: string;
    capability: string;
    state: string;
    status: string;
    validated_at: string | null;
    fallback: string;
  };
  /** Por que não há lista. `null` quando `objectives` veio preenchido. */
  reason: string | null;
};

export type PaidMediaPlanResponse = {
  plan: PlanoNaTela;
  gate: PortaoPago & {
    preview: boolean;
    evaluated_at: string;
    /** De onde veio o resultado do Release Gate, e quando. */
    release_source: 'ultimo_registro' | 'nunca_avaliado';
    release_evaluated_at: string | null;
  };
  envelope: EnvelopeNaTela;
  budget_impact: ImpactoNoEnvelope;
  provider_configured: boolean;
};

export type EnsaioDeEnvio = {
  binding_id: string;
  audience_name: string;
  meta_ad_account_id: string;
  records_selected: number;
  records_excluded: number;
  records_duplicated: number;
  /** O número que a confirmação mostra: é o que vai sair. */
  records_that_will_be_sent: number;
  excluded_by_reason: { reason: string; label: string; count: number }[];
};

export type BindingsResponse = {
  bindings: {
    binding_id: string;
    segment_id: string;
    segment_name: string | null;
    segment_status: string | null;
    meta_ad_account_id: string;
    meta_custom_audience_id: string | null;
    audience_name: string;
    sync_mode: string;
    status: string;
    status_label: string;
    never_synced: boolean;
    last_sync_at: string | null;
    last_run: {
      sync_id: string;
      status: string;
      started_at: string;
      completed_at: string | null;
      records_selected: number;
      records_excluded: number;
      records_duplicated: number;
      records_submitted: number;
      provider_accepted_count: number | null;
      error_code: string | null;
      error_message: string | null;
      requested_by: string;
    } | null;
    created_at: string;
  }[];
  provider_configured: boolean;
};

// ---- Experimentos pagos (Paid Media Module SPEC v1.0 §26) ------------------

export type ExperimentoNaTela = {
  id: string;
  name: string;
  kind: string;
  comparison: string | null;
  hypothesis: string;
  isolated_variable: string;
  changed_variables: string[];
  assigned_objective: string;
  primary_metric: string;
  secondary_metrics: string[];
  minimum_observation_hours: number | null;
  stop_rule: string | null;
  budget_ceiling: Dinheiro | null;
  minimum_sample_size: number;
  state: string;
  result: string | null;
  result_reason: string | null;
  conclusion_strength: string | null;
  concluded_at: string | null;
  created_at: string;
  arm_labels: string[];
  latest_signal_id: string | null;
};

export type BracoNaTela = {
  label: string;
  campaign_id: string;
  campaign_name: string | null;
  campaign_state: string | null;
  budget: Dinheiro | null;
};

export type SinalNaTela = {
  id: string;
  observation: string;
  strength: string;
  sample_size: number;
  confounders: string[];
  evidence_refs: string[];
  created_at: string;
};

export type TetoDeProcedencia = { provenance: string; strength_ceiling: string };

/**
 * A lista, com as tres listas fechadas que a tela precisa para desenhar o
 * formulario.
 *
 * Elas vem do SERVIDOR e nao do front: duas copias da mesma lista divergem na
 * segunda semana, e a que envelhece e sempre a do formulario.
 */
export type ListaDeExperimentos = {
  experiments: ExperimentoNaTela[];
  required_fields: string[];
  allowed_comparisons: string[];
  provenance_ceilings: TetoDeProcedencia[];
};

export type ExperimentoDetalhado = ExperimentoNaTela & {
  arms: BracoNaTela[];
  signals: SinalNaTela[];
};

export type CorpoDeExperimento = {
  name: string;
  comparison: string;
  hypothesis: string;
  assigned_objective: string;
  primary_metric: string;
  secondary_metrics: string[];
  minimum_observation_hours: number;
  stop_rule: string;
  budget_ceiling_minor: number;
  minimum_sample_size: number;
  changed_variables: string[];
  arm_a_campaign_id: string;
  arm_b_campaign_id: string;
};

export const api = {
  // ---- autenticacao -------------------------------------------------------
  //
  // `me` e a UNICA forma sem ambiguidade de o front decidir entre login e
  // painel: uma rota de dados confundiria "sem sessao" (401) com "sem aquele
  // escopo" (403), e quem tivesse papel sem o escopo veria login para sempre.
  me: () => request<MeResponse>('/admin/v1/auth/me'),
  requestOtp: (email: string) =>
    request<{ ok: true; message: string }>('/admin/v1/auth/otp/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  verifyOtp: (email: string, codigo: string) =>
    request<{ ok: true; expires_in: number }>('/admin/v1/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ email, codigo }),
    }),
  logout: () => request<{ ok: true }>('/admin/v1/auth/logout', { method: 'POST' }),
  operations: () => request<OperationsResponse>('/admin/v1/operations'),
  configuration: () => request<ConfigurationResponse>('/admin/v1/configuration'),

  /**
   * Envia um comando administrativo.
   *
   * A chave de idempotencia e gerada AQUI, uma por tentativa do usuario, e nao
   * no servidor: e o clique duplo que ela precisa absorver, e um valor gerado
   * no servidor absorveria nada. `crypto.randomUUID` existe em todo navegador
   * que este painel suporta.
   */
  sendCommand: (body: {
    command_type: string;
    target_ref: { object_type: string; object_id: string };
    reason: string;
    payload?: Record<string, unknown>;
    expected_revision?: number | null;
  }) =>
    request<CommandResponse>('/admin/v1/commands', {
      method: 'POST',
      headers: { 'idempotency-key': crypto.randomUUID() },
      body: JSON.stringify({
        ...body,
        client_context: { surface: 'back_office', view: 'controles' },
      }),
    }),
  /**
   * Exportacao dos dados do titular (LGPD).
   *
   * POST, e nao GET, de proposito: o handle e a justificativa vao no CORPO. Dado
   * pessoal nunca entra em query string, que e o que aparece em log de proxy, em
   * historico de navegador e em referer.
   */
  exportarTitular: (body: { handle: string; justification: string }) =>
    request<TitularExportResponse>('/admin/v1/lgpd/titular/exportacao', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  kpis: () => request<KpisResponse>('/admin/v1/kpis'),
  agents: () => request<AgentsResponse>('/admin/v1/agents'),
  attention: () => request<AttentionResponse>('/admin/v1/attention'),
  content: () => request<ContentListResponse>('/admin/v1/content'),
  contentDetail: (id: string) =>
    request<ContentDetailResponse>(`/admin/v1/content/${encodeURIComponent(id)}`),
  integrations: () => request<IntegrationsResponse>('/admin/v1/integrations'),
  /*
    Roda a sondagem de capacidades da Meta. É POST porque escreve o registro que
    decide se gasto sai depois, ainda que na Meta ela só leia.
  */
  rodarSondagemDaMeta: () =>
    request<SondagemDaMetaResponse>('/admin/v1/integrations/meta/probe', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  renders: () => request<RendersResponse>('/admin/v1/renders'),
  episodes: () => request<EpisodesResponse>('/admin/v1/episodes'),
  learning: () => request<LearningResponse>('/admin/v1/learning'),
  audience: () => request<AudienceResponse>('/admin/v1/audience'),

  // ---- Audience & Lists ---------------------------------------------------
  audienceSources: () => request<AudienceSourcesResponse>('/admin/v1/audience/sources'),
  audienceSegments: () => request<AudienceSegmentsResponse>('/admin/v1/audience/segments'),
  /**
   * A previa do §183 antes de salvar. Nao grava nada.
   *
   * POST, e nao GET com o filtro na query: a arvore de filtro pode citar nome
   * de empresa e cargo, e query string aparece em log de proxy, em historico de
   * navegador e em referer.
   */
  audiencePreview: (body: { source_id: string; filter: unknown }) =>
    request<PreviewEnvelope>('/admin/v1/audience/segments/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  audienceSaveSegment: (body: {
    name: string;
    description?: string;
    source_id: string;
    mode: string;
    filter: unknown;
  }) =>
    request<PreviewEnvelope & { segment: AudienceSegmentsResponse['segments'][number] }>(
      '/admin/v1/audience/segments',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  audienceRefreshSegment: (id: string) =>
    request<PreviewEnvelope & { segment_id: string; refreshed: boolean }>(
      `/admin/v1/audience/segments/${encodeURIComponent(id)}/refresh`,
      { method: 'POST' },
    ),

  // ---- Importação de audiência por arquivo (§186, §187) -------------------
  //
  // A prévia NÃO grava: ela é chamada a cada ajuste de mapeamento, e uma prévia
  // que gravasse encheria o banco de importações abandonadas com dado pessoal
  // dentro. Quem grava é o commit, e ele exige o número conferido mais a
  // declaração de base legal.
  /** Lê, mapeia, valida e deduplica sem gravar nada. */
  audienceImportPreview: (body: CorpoDaPreviaDeImportacao) =>
    request<AudienceImportPreviewResponse>('/admin/v1/audience/imports/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * Grava a importação.
   *
   * `confirmed_rows` é o número que a pessoa VIU, e o servidor recusa sem
   * gravar se o arquivo ou o mapeamento mudaram desde a prévia: o número
   * aprovado deixaria de descrever o que entraria.
   */
  audienceImportCommit: (
    body: CorpoDaPreviaDeImportacao & { confirmed_rows: number; lawful_basis_note: string },
  ) =>
    request<AudienceImportCommitResponse>('/admin/v1/audience/imports', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  audienceImports: () => request<AudienceImportsResponse>('/admin/v1/audience/imports'),
  // ---- Mídia paga ---------------------------------------------------------
  //
  // Toda ação que gasta dinheiro manda `confirmed_budget_minor`, e o envio de
  // audiência manda `confirmed_records`. Não é formalidade: é o servidor
  // conferindo que a pessoa decidiu sobre o valor que ainda vale. Se mudou
  // entre ver e clicar, ele recusa e devolve o número novo.
  paidMedia: () => request<PaidMediaResponse>('/admin/v1/paid-media'),
  /*
    Chamada SEPARADA da área inteira porque é a única coisa da tela que muda
    quando alguém sonda a conta: quem acabou de conectar recarrega só o
    catálogo, sem repuxar cem planos e cem campanhas.
  */
  paidMediaObjetivos: () => request<ObjetivosDeCampanhaResponse>('/admin/v1/paid-media/objectives'),
  paidMediaPlan: (id: string) =>
    request<PaidMediaPlanResponse>(`/admin/v1/paid-media/plans/${encodeURIComponent(id)}`),
  paidMediaCriarPlano: (body: {
    source_content_id: string;
    audience_segment_id: string;
    meta_audience_binding_id?: string;
    objective: string;
    budget_kind: string;
    /** Na unidade MAIOR (reais). O servidor converte; a tela não faz conta. */
    budget_amount: number;
    start_at?: string;
    end_at?: string;
    paid_copy?: string;
    paid_cta?: string;
    rights_paid_permission?: string;
  }) =>
    request<{ plan: PlanoNaTela }>('/admin/v1/paid-media/plans', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  paidMediaAprovarPlano: (id: string, body: { reason: string; confirmed_budget_minor: number }) =>
    request<{ plan_id: string; status: string }>(
      `/admin/v1/paid-media/plans/${encodeURIComponent(id)}/approve`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  paidMediaRejeitarPlano: (id: string, body: { reason: string }) =>
    request<{ plan_id: string; status: string }>(
      `/admin/v1/paid-media/plans/${encodeURIComponent(id)}/reject`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  paidMediaCriarCampanha: (
    id: string,
    body: { name: string; ad_account_id: string; confirmed_budget_minor: number },
  ) =>
    request<{
      campaign_id: string;
      provider_campaign_id: string;
      already_existed: boolean;
      gate: PortaoPago | null;
    }>(`/admin/v1/paid-media/plans/${encodeURIComponent(id)}/campaigns`, {
      method: 'POST',
      // A chave de idempotência é gerada AQUI, uma por tentativa: é o clique
      // duplo que ela precisa absorver, e criar campanha duas vezes é reservar
      // o orçamento duas vezes.
      headers: { 'idempotency-key': crypto.randomUUID() },
      body: JSON.stringify(body),
    }),
  paidMediaAtivarCampanha: (id: string, body: { confirmed_budget_minor: number }) =>
    request<{ campaign_id: string; provider_effective_state: string | null }>(
      `/admin/v1/paid-media/campaigns/${encodeURIComponent(id)}/activate`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  paidMediaPausarCampanha: (id: string, body: { reason: string }) =>
    request<{ campaign_id: string; provider_effective_state: string | null }>(
      `/admin/v1/paid-media/campaigns/${encodeURIComponent(id)}/pause`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // ---- Experimentos pagos (§26) -------------------------------------------
  experimentos: () => request<ListaDeExperimentos>('/admin/v1/experiments'),
  experimento: (id: string) =>
    request<ExperimentoDetalhado>(`/admin/v1/experiments/${encodeURIComponent(id)}`),
  experimentoCriar: (body: CorpoDeExperimento) =>
    request<{ experiment_id: string; state: string }>('/admin/v1/experiments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  experimentoAbandonar: (id: string, body: { reason: string }) =>
    request<{ experiment_id: string; state: string }>(
      `/admin/v1/experiments/${encodeURIComponent(id)}/abandon`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // ---- Vínculos de audiência com a Meta -----------------------------------
  audienceBindings: () => request<BindingsResponse>('/admin/v1/audience/bindings'),
  /** O ensaio: conta quem sairia, e NÃO envia nada. */
  audienceSyncPreview: (id: string) =>
    request<EnsaioDeEnvio>(`/admin/v1/audience/bindings/${encodeURIComponent(id)}/sync/preview`, {
      method: 'POST',
    }),
  audienceSync: (id: string, body: { confirmed_records: number }) =>
    request<{
      sync_id: string;
      status: string;
      records_selected: number;
      records_excluded: number;
      records_duplicated: number;
      records_submitted: number;
      provider_accepted_count: number | null;
      meta_custom_audience_id: string | null;
      error_code: string | null;
      already_existed: boolean;
    }>(`/admin/v1/audience/bindings/${encodeURIComponent(id)}/sync`, {
      method: 'POST',
      headers: { 'idempotency-key': crypto.randomUUID() },
      body: JSON.stringify(body),
    }),

  community: () => request<CommunityResponse>('/admin/v1/community'),
  commercial: () => request<CommercialResponse>('/admin/v1/commercial'),
  executive: () => request<ExecutiveResponse>('/admin/v1/executive'),
  motor: () => request<MotorResponse>('/admin/v1/motor'),
  /**
   * Entrega uma oportunidade ao motor.
   *
   * Devolve `enfileirado: false` com motivo `duplicado` no clique repetido, e
   * isso nao e erro: e a idempotencia por chave derivada do conteudo.
   */
  motorAvaliar: (oportunidadeId: string) =>
    request<{ enfileirado: boolean; motivo: string | null; fila: string }>(
      `/admin/v1/motor/oportunidades/${encodeURIComponent(oportunidadeId)}/avaliar`,
      { method: 'POST' },
    ),
  publicationMetrics: (id: string) =>
    request<PublicationMetricsResponse>(`/admin/v1/publications/${encodeURIComponent(id)}/metrics`),
  version: () =>
    request<{ app_version: string; git_sha: string; spec_version: string }>('/version'),
};
